import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import { concatMap } from 'rxjs'

import { Hunt } from '@cyborg-monorepo/hunter/common'
import {
  BadgeComponent,
  ButtonComponent,
  ButtonMenuComponent,
  DropdownMenuItemComponent,
  ErrorComponent,
  TagComponent,
  TypographyComponent,
  BreadcrumbListComponent,
  DialogService,
  SegmentedProgressBarComponent,
} from '@hyperkit'

import {
  NavigationService,
  BreadcrumbService,
  HuntViewService,
  HuntEditorService,
  SessionService,
} from '@hunt/services'

import { HuntAPIService } from '../../shared/services'
import { HuntEditorComponent } from '../details/components/hunt-editor'
import { HuntPackageListItem } from '../details/components/packages'
import { ArchiveHuntDialogComponent } from '../dialogs/archive-hunt-dialog/archive-hunt-dialog.component'
import { EditHuntDialogComponent } from '../dialogs/edit-hunt-dialog/edit-hunt-dialog.component'

@Component({
  standalone: true,
  selector: 'cyborg-monorepo-hunt-workbench',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    ButtonComponent,
    TypographyComponent,
    BadgeComponent,
    TagComponent,
    ErrorComponent,
    ButtonMenuComponent,
    DropdownMenuItemComponent,
    BreadcrumbListComponent,
    HuntPackageListItem,
    HuntEditorComponent,
    SegmentedProgressBarComponent,
  ],
  templateUrl: './hunt-workbench.component.html',
  styleUrls: ['./hunt-workbench.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HuntWorkbenchComponent {
  private readonly navSvc = inject(NavigationService)
  private readonly breadcrumb = inject(BreadcrumbService)
  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  readonly huntView = inject(HuntViewService)
  readonly huntEditor = inject(HuntEditorService)
  private readonly api = inject(HuntAPIService)
  private readonly router = inject(Router)
  private readonly dialogService = inject(DialogService)
  public error: string

  /** Reactive breadcrumbs */
  public readonly breadcrumbs = this.breadcrumb.workbench

  constructor() {
    // If a local backup was found that diverges from the server's copy
    // (e.g. a previous autosave failed silently), ask before applying it.
    // Never auto-applied -- see HUN-1091 for why that's unsafe.
    effect(() => {
      const draft = this.session.recoverableDraft()
      if (!draft) return

      const shouldRecover = window.confirm(
        'A more recent unsaved version of this hunt was found on this ' +
          'device, possibly from a save that failed to complete. ' +
          'Recover it?',
      )

      if (shouldRecover) {
        this.session.recoverDraft()
      } else {
        this.session.dismissDraft()
      }
    })
  }

  /** Navigate into a different package on the same hunt */
  navigateToHuntPackage(pkgId: string) {
    this.navSvc.navigateToHuntPackage(pkgId)
  }

  /** Open the “Edit Hunt” dialog */
  async editHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref = this.dialogService.open(EditHuntDialogComponent, { data: hunt })
    ref.afterClosed().subscribe(async (hunt) => {
      await this.session.loadHunt((hunt as unknown as Hunt).id, true)
    })
  }

  /** Archive the hunt and go back to list */
  async archiveHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref = this.dialogService.open<Hunt>(ArchiveHuntDialogComponent, {
      data: hunt,
    })

    ref.afterClosed().subscribe(async (archived: Hunt | undefined) => {
      if (archived) {
        await this.session.loadHunt(archived.id, true)
        this.router.navigate(['/hunt/hunts'])
      }
    })
  }

  /** Persist and “close” the hunt, then jump to the report editor */
  async closeHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    this.api
      .updateHunt(hunt.id, hunt)
      .pipe(concatMap(() => this.api.closeHunt(hunt.id)))
      .subscribe(async (updated) => {
        await this.session.loadHunt(updated.id, true)
        this.router.navigate(['/hunt', updated.id, 'edit-report'])
      })
  }

  /** Handle the dropdown menu actions */
  handleMenuAction(event: { value: 'update' | 'archive' }) {
    if (event.value === 'update') this.editHunt()
    else if (event.value === 'archive') this.archiveHunt()
  }
}
